import { FC, MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AppraisalHeader from '../../components/custom_components/Appraisal/AppraisalHeader'
import BackgroundBox from '../../components/custom_components/BackgroundBox'
import PerButtonCircle from '../../components/custom_components/ButtonCircle'
import { PerEmpty } from '../../components/custom_components/Empty'
import PerFlexRow from '../../components/custom_components/FlexRow'
import { NewLoadingAndErrors } from '../../components/custom_components/LoadingAndErrors/NewLoadingAndErrors'
import MeetingBackground from '../../components/custom_components/Meeting/MeetingBackground'
import PerSpacer from '../../components/custom_components/Spacer'
import PerText from '../../components/custom_components/Text'
import { useAuth } from '../../context/auth-context'
import { useScoreGridsOverview } from '../../graphql/score-grid/hooks/useScoreGridsOverview'
import { EmployeeDisplayTinyInfo, MeetingDashboardFragment, SkillEmployeeStatusDto } from '../../types'
import { EvaluationScaleInformation } from '../appraisal/evaluation/EvaluationScaleInformation'
import { AppraisalInviteButton } from '../meeting/invite/AppraisalInviteButton'
import { RequestMeetingInviteButton } from '../meeting/invite/RequestMeetingInviteButton'
import { EvaluationOverviewContainer } from './EvaluationOverviewContainer'
import { EvaluationScoreGridContainer } from './EvaluationScoreGridContainer'
import { OwnGrowthGridCategory } from './OwnGrowthGridCategory'

type Props = {
  employee?: EmployeeDisplayTinyInfo
  employeeEditable: boolean
  view: 'OwnGrowthPage' | 'PublicProfilePage'
  plannedMeeting?: MeetingDashboardFragment
  employeeSkillStatus: SkillEmployeeStatusDto | null
}

export const OwnGrowthEvaluationContainer: FC<Props> = ({ employee, employeeEditable, view, plannedMeeting, employeeSkillStatus }) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const [activeCategory, setActiveCategory] = useState(OwnGrowthGridCategory.Overview)
  const employeeId = employee ? employee.id : user.employeeId
  const privateView = !employee

  const { data, error, loading } = useScoreGridsOverview(employeeId)

  if (loading || error || !data) {
    return (
      <BackgroundBox r height={view === 'PublicProfilePage' ? 'profileView' : 'withTabs'}>
        <NewLoadingAndErrors isLoading={loading} error={error} hasNoData={!data} showSpinner t={t} />
      </BackgroundBox>
    )
  }

  const emptyTableActionButton = () => {
    if (employeeEditable) {
      if (privateView) {
        return <RequestMeetingInviteButton context="OwnGrowth" />
      } else {
        return <AppraisalInviteButton employeeId={employee.id} employeeSkillStatus={employeeSkillStatus} />
      }
    } else return <></>
  }

  if (
    !data.evaluationFromLastTwoAppraisals ||
    (!data.evaluationFromLastTwoAppraisals.scoreGoals.some((value) => value.resultEvaluation || value.lastResultEvaluation) && !privateView)
  ) {
    return (
      <BackgroundBox color="white75" r>
        <PerEmpty
          icon="ownGrowthNo_stroke"
          heading={t(`ownGrowthView.noData.${privateView ? 'employee' : 'profile'}.title`)}
          button={!plannedMeeting && emptyTableActionButton()}
        />
      </BackgroundBox>
    )
  }

  const tabs = Object.keys(OwnGrowthGridCategory).map((entry) => {
    return { id: entry, title: t(`ownGrowthView.grid.category.${entry}`) }
  })

  const handleCategoryChange = (e: MouseEvent<HTMLDivElement>) => {
    setActiveCategory(e.currentTarget.dataset.category as OwnGrowthGridCategory)
  }

  return (
    <BackgroundBox rt color="transparent" noPadding noShadow>
      <AppraisalHeader noShadow>
        <PerFlexRow direction="row" align="middle" justify="space-between" className="fullWidth">
          <div className="tab-bar">
            {tabs.map((tab) => (
              <PerSpacer key={tab.id} ml="s" mr="s" display="inline">
                <div
                  className={`tab-bar__tab ${activeCategory === tab.id ? 'tab-bar__tab--active' : ''}`}
                  onClick={handleCategoryChange}
                  data-category={tab.id}
                >
                  <PerText display="inline-block">{tab.title}</PerText>
                </div>
              </PerSpacer>
            ))}
          </div>
          <PerButtonCircle icon="info_stroke" color="secondary" variant="pastel" tooltip={<EvaluationScaleInformation />} size="small" />
        </PerFlexRow>
      </AppraisalHeader>
      <MeetingBackground id="evaluation-background-box" heigth={privateView ? 'withDoubleTabs' : 'withProfileHeaderAndTabs'} noShadow>
        <PerFlexRow direction="column" justify="space-between" className="per-evaluation-Content--fullHeight">
          {activeCategory === OwnGrowthGridCategory.Overview ? (
            <EvaluationOverviewContainer evaluationOverview={data.evaluationFromLastTwoAppraisals} privateView={privateView} />
          ) : (
            <EvaluationScoreGridContainer
              evaluationOverview={data.evaluationFromLastTwoAppraisals}
              activeCategory={activeCategory}
              privateView={privateView}
            />
          )}
        </PerFlexRow>
      </MeetingBackground>
    </BackgroundBox>
  )
}
